import { useNextStep } from "nextstepjs";
import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { usePlayground } from "@/components/Playground/playground-store";
import { usePlaygroundTourContext } from "@/components/Playground/tour/context";
import { PLAYGROUND_TOUR_NAME, playgroundTourSteps } from "@/components/Playground/tour/constants";
import { scrollStepTargetIntoView } from "@/components/Playground/tour/helpers";
import { applyTourPreconfig } from "@/components/Playground/tour/mocks";
import useLocalStorageState, { LOCAL_STORAGE_KEYS } from "@/hooks/useLocalStorageState";

export const TOUR_QUERY_PARAM = "tour";

/**
 * Mirrors `?tour` into the tour state. Must be used inside
 * `PlaygroundTourProvider` and `NextStepProvider`.
 */
export const useTourUrlParam = () => {
  const tour = usePlaygroundTourContext();
  const playground = usePlayground();
  const { startNextStep } = useNextStep();
  const [searchParams, setSearchParams] = useSearchParams();
  const [visitedAt, setVisitedAt] = useLocalStorageState<string | null>(
    LOCAL_STORAGE_KEYS.TOUR_PLAYGROUND_VISITED,
    null,
  );

  // Refs so the URL effect doesn't re-fire on every tour state mutation.
  const tourRef = useRef(tour);
  tourRef.current = tour;
  const playgroundRef = useRef(playground);
  playgroundRef.current = playground;

  const hasTourParam = searchParams.get(TOUR_QUERY_PARAM) !== null;
  // In render scope so the effect retries when session init finishes.
  // Requires the caller to be a MobX `observer`.
  const isPendingSessionInit = playground.isPendingSessionInitialization;

  // `tour.isActive` is the idempotency guard: a second invocation is a no-op.
  useEffect(() => {
    if (isPendingSessionInit) {
      return;
    }
    if (hasTourParam && !tourRef.current.isActive) {
      // Mark visited on every start path so the first-visit nudge never
      // re-arms for users who landed via a shared `?tour` link.
      setVisitedAt(new Date().toISOString());
      const firstStepSelector = playgroundTourSteps[0]?.steps[0]?.selector;
      applyTourPreconfig(playgroundRef.current);
      scrollStepTargetIntoView(firstStepSelector);
      tourRef.current.start();
      startNextStep(PLAYGROUND_TOUR_NAME);
      return;
    }
    if (!hasTourParam && tourRef.current.isActive) {
      tourRef.current.end();
    }
  }, [hasTourParam, isPendingSessionInit, startNextStep, setVisitedAt]);

  // First-visit nudge: write `?tour` once so the URL effect drives the start
  // path. `hasNudgedRef` survives StrictMode's double-invoke of the effect.
  const hasNudgedRef = useRef(false);
  useEffect(() => {
    if (hasNudgedRef.current) {
      return;
    }
    hasNudgedRef.current = true;
    if (visitedAt !== null || hasTourParam) {
      return;
    }
    setVisitedAt(new Date().toISOString());
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set(TOUR_QUERY_PARAM, "");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addTourParam = useCallback(() => {
    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.set(TOUR_QUERY_PARAM, "");
    setSearchParams(urlSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const removeTourParam = useCallback(() => {
    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.delete(TOUR_QUERY_PARAM);
    setSearchParams(urlSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return { hasTourParam, addTourParam, removeTourParam };
};
